import type { HTMLMotionProps } from 'framer-motion'

import { forwardRef } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'

type Variant = 'red' | 'yellow' | 'ghost'
type Size = 'md' | 'lg'

interface ButtonProps extends HTMLMotionProps<'button'> {
    variant?: Variant
    size?: Size
}

const base = 'inline-flex items-center justify-center gap-2 rounded-pill font-display font-semibold tracking-wide transition-shadow focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-yellow/60 disabled:cursor-not-allowed disabled:opacity-60'

const variants: Record<Variant, string> = {
    red: 'bg-brand-red text-white shadow-soft hover:shadow-[0_14px_36px_-10px_rgba(217,68,67,0.55)]',
    yellow: 'bg-brand-yellow text-brand-red-dark shadow-soft-yellow hover:shadow-[0_14px_36px_-10px_rgba(251,191,36,0.6)]',
    ghost: 'bg-white/70 text-brand-red-dark hover:bg-white'
}

const sizes: Record<Size, string> = {
    md: 'h-11 px-5 text-sm',
    lg: 'h-14 px-7 text-base'
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant = 'red', size = 'md', children, ...props }, ref) => (
    <motion.button
        ref={ref}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        transition={{ type: 'spring', stiffness: 400, damping: 24 }}
        className={cn(base, variants[variant], sizes[size], className)}
        {...props}
    >
        {children}
    </motion.button>
))

Button.displayName = 'Button'

export default Button
